import {Component, HostListener, OnInit} from '@angular/core';
import {SelectItem} from "primeng/api";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {ConversorsService} from "../../../../services/conversors.service";
import {DataType} from "../../../../model/data-type";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {DefaultPropertyType} from "../../../../model/default-property-type";
import {DefaultPropertyTypesService} from "../../../../services/default-property-types.service";
import {DefaultPropertyTypePersistRequest} from "../../../../model/requests/default-property-type-persist-request";

@Component({
  selector: 'app-default-property-types-form',
  templateUrl: './default-property-types-form.component.html',
  styleUrls: ['./default-property-types-form.component.scss']
})
export class DefaultPropertyTypesFormComponent implements OnInit {

  public type: DefaultPropertyType;

  public dataTypes: SelectItem[];

  public typeForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { oldName: string, oldUnit: string };

  public units: any[];
  public filteredUnits: any[];
  public convert = this.conversorsService.convert;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private defaultPropertyTypesService: DefaultPropertyTypesService,
              private commonMessagesService: CommonMessagesService,
              private conversorsService: ConversorsService) {
    this.dataTypes = [
      {label: 'forms.sensorType.STRING', value: DataType.STRING},
      {label: 'forms.sensorType.NUMBER', value: DataType.NUMBER},
      {label: 'forms.sensorType.BOOLEAN', value: DataType.BOOLEAN},
      {label: 'forms.sensorType.COORDINATES', value: DataType.COORDINATES},
      {label: 'forms.sensorType.ANALOG_SIGNAL', value: DataType.ANALOG_SIGNAL},
      {label: 'forms.sensorType.DIGITAL_SIGNAL', value: DataType.DIGITAL_SIGNAL}
    ];
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[1].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[1].path, urlSegment[2].path);
      }
    });
  }

  loadCreationForm(): void {
    this.type = new DefaultPropertyType();
    this.createForm(this.type);
  }

  loadEditionForm(name: string, unit: string): void {
    this.defaultPropertyTypesService.getDefaultPropertyType(name, unit).toPromise().then(response => {
      this.type = response.defaultPropertyType;
      this.editInfo = {oldName: this.type.name, oldUnit: this.type.unit};
      this.createForm(this.type);
    });
  }

  private createForm(type: DefaultPropertyType): void {
    this.typeForm = this.formBuilder.group({
      name: [type.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      unit: [type.unit, [Validators.required, Validators.maxLength(255)]],
      dataType: [type.dataType, [Validators.required, Validators.maxLength(255)]]
    });
    this.typeForm.markAsPending();
  }

  persistTypeData(): void {
    if (!this.typeForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.defaultPropertyTypesService.createDefaultPropertyType(persistData).toPromise();
    } else {
      promise = this.defaultPropertyTypesService.editDefaultPropertyType(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/hw-catalog`], {state: {showView: 'property-types'}});
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name + ' - ' +  persistData.unit);
      this.persistingChanges = false;
    });
  }

  public cancel(): void {
    this.router.navigate([`/system/hw-catalog`], {state: {showView: 'property-types'}});
  }

  private buildPersistData(): DefaultPropertyTypePersistRequest {
    const formValue = this.typeForm.getRawValue();
    return {
      name: formValue.name,
      unit: formValue.unit,
      dataType: formValue.dataType,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null,
      oldUnit: this.mode === 'edit' ? this.editInfo.oldUnit : null
    };
  }

  filterUnit(event): void {
    const units = this.convert().possibilities();
    let filtered: any[] = [];
    let query = event.query;
    for (let i = 0; i < units.length; i++) {
      let unit = units[i];
      if (unit.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        filtered.push(unit);
      }
    }
    this.filteredUnits = filtered;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.typeForm.untouched;
  }

}
